import { NotificationAlertLowShieldOutlineBaseIconRaw } from '@tableau/icons/NotificationAlertLowShieldOutlineBaseIcon/raw';
import { NotificationLowOutlineIconRaw } from '@tableau/icons/NotificationLowOutlineIcon/raw';
import { ReactiveRawIconRegistry } from '@tableau/icons/reactive';
import { BodyCell, HeaderColumn } from '@tableau/widgets-datagrid';
import React from 'react';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

import { cssClass } from '../../../style';

const CssPrefix = 'shared-widgets-datagrid';

//////////////////////////////////////////////////////////
//  Copied from common/react/components/dataGrid/StyledCellWrapper.tsx
//  Needed to ship with two versions of data grid
/////////////////////////////////////////////////////////


/*
 * TODO: TFS 769624
 *
 * The following cell styles are copied from reactDataGrid.styl.
 *
 * When we upgrade VizPortal to Data Grid 2/3, the cellStyle/headerStyle/rowStyle props are gone,
 * so we will need to wrap every cell formatter in one of these wrappers to get consistent styles
 * on all the grids and also be able to apply the style overrides we need for some special cells.
 *
 * We are using these wrappers for some cells already to avoid passing the cellStyle props into the
 * data grid (which will make it easier to upgrade later), but since the cell/header styles are still
 * applied via CSS to the parent div, the styles that would cause conflict are commented out here.
 *
 * Once the data grid upgrade is complete and these wrappers are used on all the cell formatters,
 * uncomment the styles here and remove the relevant classes (tb-react-dg-hcell, tb-react-dg-bcell)
 * from reactDataGrid.styl.
 */

export const DefaultBodyCellStyle = cssClass(CssPrefix, 'bcell-wrapper', {
  paddingLeft: CommonStyles.Sizes.MediumSpace,
  paddingRight: CommonStyles.Sizes.MediumSpace,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'middle'
});

const DefaultHeaderCellStyle = cssClass(CssPrefix, 'hcell-wrapper', {
  // fontWeight: 'bold',
  // height: CommonStyles.Sizes.DataGridHeaderHeight,
  // lineHeight: CommonStyles.Sizes.DataGridHeaderHeight + 'px',
  // fontSize: CommonStyles.Sizes.LargeFontSize,
  // color: CommonStyles.Colors.Grey51
});

/**
 * Stable bridge component for cell formatters. The datagrid calls cell formatters as plain
 * functions (React.Factory), so the outer wrapper has no React fiber. This bridge is rendered
 * as JSX, providing a fiber for hook-using formatters (e.g. LastRefreshedCell). Because it's
 * defined at module scope its identity never changes — React reuses the fiber even when the
 * formatter closure is recreated on column rebuild, preventing unmount/remount and focus loss.
 */
function FormatterBridge<TProps>(bridgeProps: { formatter: React.Factory<TProps>, props: TProps }) {
  return bridgeProps.formatter(bridgeProps.props);
}

/**
 * This wrapper applies VizPortal's default styles to data grid body cells, allowing them to be
 * overridden if necessary. All cell formatters in VizPortal should be enclosed in this wrapper
 * to ensure consistent visual styling.
 */
export function StyledCellFormatter(OriginalCellFormatter: React.Factory<BodyCell.Props>,
                                    styleOverrides?: React.CSSProperties
): React.Factory<BodyCell.Props> {

  return function VizPortalDataGridBodyCell(props: BodyCell.Props) {
    return (
      <div className={ DefaultBodyCellStyle } style={ styleOverrides } >
        <FormatterBridge formatter={OriginalCellFormatter} props={props} />
      </div>
    );
  };
}


/**
 * This wrapper applies VizPortal's default styles to data grid header cells, allowing them to be
 * overridden if necessary. All cell formatters in VizPortal should be enclosed in this wrapper
 * to ensure consistent visual styling.
 */
export function StyledHeaderFormatter(OriginalHeaderFormatter: React.Factory<HeaderColumn.HeaderFormatterProps>,
                                      styleOverrides?: React.CSSProperties
): React.Factory<HeaderColumn.HeaderFormatterProps> {

  return function VizPortalDataGridHeaderCell(props: HeaderColumn.HeaderFormatterProps) {
    return (
      <div className={ DefaultHeaderCellStyle } style={ styleOverrides } >
        <FormatterBridge formatter={OriginalHeaderFormatter} props={props} />
      </div>
    );
  };
}


export function CenterAlignedHeader(headerFormatter: React.Factory<HeaderColumn.HeaderFormatterProps>) {
  return StyledHeaderFormatter(headerFormatter, CommonStyles.Layout.TextAlignCenter);
}

export function RightAlignedHeader(headerFormatter: React.Factory<HeaderColumn.HeaderFormatterProps>) {
  return StyledHeaderFormatter(headerFormatter, CommonStyles.Layout.TextAlignRight);
}

export function CenterAlignedCell(cellFormatter: React.Factory<BodyCell.Props>) {
  return StyledCellFormatter(cellFormatter, CommonStyles.Layout.TextAlignCenter);
}

export function RightAlignedCell(cellFormatter: React.Factory<BodyCell.Props>) {
  return StyledCellFormatter(cellFormatter, CommonStyles.Layout.TextAlignRight);
}

export function AlertCellHeader(headerFormatter: React.Factory<HeaderColumn.HeaderFormatterProps>) {
  return StyledHeaderFormatter(headerFormatter, {
    backgroundImage: `var(${ReactiveRawIconRegistry.register(NotificationAlertLowShieldOutlineBaseIconRaw, 18)})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: CommonStyles.Sizes.NormalIconBackgroundSize,
    width: CommonStyles.Sizes.NormalIconSize + 2 * CommonStyles.Sizes.MediumSpace
  });
}

export function DataQualityWarningDisabledCellHeader(headerFormatter: React.Factory<HeaderColumn.HeaderFormatterProps>) {
  const icon = 'url("data:image/svg+xml,'+encodeURIComponent(NotificationLowOutlineIconRaw(16, '#AAAAAA'));
  return StyledHeaderFormatter(headerFormatter, {
    backgroundImage: icon,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    width: CommonStyles.Sizes.NormalIconSize + 2 * CommonStyles.Sizes.MediumSpace
  });
}
